import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import TableCDS from '@sharedCR/coreDesignSystem/Table/TableCDS';
import { LoanApplicationTabId } from 'app/pages/loanApplication/LoanApplication.constants';
import { CUSTOM_FIELDS_TARGET } from 'app/shared/components/customFields/CustomFieldsSection/CustomFieldsSection.constants';
import useEntitiesCustomFieldsPendingChangesMap from 'app/shared/components/customFields/CustomFieldsSection/hooks/useEntitiesCustomFieldsPendingChangesMap';
import withLoading from 'hocs/withLoading';
import _ from 'lodash';

import useTableCellPendingChangeAuthors from 'hooks/useTableCellPendingChangeAuthors';
import { calculatePendingChanges, getNewAndDeletedEntities } from 'utils/pendingChanges';

import { deleteLoanApplicationIncomeAction } from 'store/loanApplications/actions';
import { selectSortedBorrowerIncomes } from 'store/loanApplications/selectors';

import { normalizeIncomesPendingChanges } from '../../UpdateTableModal/Content/SectionContent/EmploymentAndIncome/EmploymentAndIncome.utils';
import {
  FINANCIAL_INFO_TABLE_NAME,
  getDataWithNotFoundMessage,
  TablePropTypes,
} from '../FinancialInfoBody.constants';
import { tableStyles } from '../FinancialInfoBody.styles';
import { getTotalPendingChangesCount } from '../FinancialInfoBody.utils';
import useNeededCountByRecord from '../hooks/useNeededCountByRecord';
import TableWrapper from '../TableWrapper';

import { getConfig } from './EmploymentAndIncome.config';

const EmploymentAndIncome = ({
  isLoading,
  onToggle,
  tableName,
  borrowerId,
  setIsLoading,
  onAddRecord,
  pendingChanges,
  chatTargets = null,
}) => {
  const incomes = useSelector(selectSortedBorrowerIncomes(borrowerId));
  const { applicationId, subtab } = useParams();

  const dispatch = useDispatch();

  const isExpanded = subtab === tableName;

  const onRemove = useCallback(
    recordId => {
      setIsLoading(true);

      dispatch(
        deleteLoanApplicationIncomeAction({
          incomeId: recordId,
          tabId: LoanApplicationTabId.INCOME_AND_EMPLOYMENT,
          applicationId,
        }),
      )
        .catch(Error)
        .finally(() => setIsLoading(false));
    },
    [dispatch, applicationId, setIsLoading],
  );

  const onAddClick = useCallback(() => onAddRecord(tableName), [onAddRecord, tableName]);

  const neededCountById = useNeededCountByRecord(tableName, incomes);
  const getCellPendingChangeAuthors = useTableCellPendingChangeAuthors();

  const config = useMemo(
    () => getConfig({ isExpanded, onRemove, chatTargets, neededCountById }),
    [isExpanded, onRemove, chatTargets, neededCountById],
  );

  const formattedData = incomes.length ? incomes : getDataWithNotFoundMessage(config)[tableName];

  const incomeIds = useMemo(() => incomes.map(({ id }) => id), [incomes]);

  const customFieldsPendingChanges = useEntitiesCustomFieldsPendingChangesMap({
    applicationId,
    target: CUSTOM_FIELDS_TARGET.INCOME,
    entityIds: incomeIds,
  });

  const pendingChangesCounts = useMemo(() => {
    const { newEntities, deletedEntities } = getNewAndDeletedEntities(incomes);

    return calculatePendingChanges(
      _.merge(
        {},
        normalizeIncomesPendingChanges({
          initialPendingChanges: pendingChanges,
          viewData: _.keyBy(incomes, 'id'),
          newEntities,
          deletedEntities,
        }),
        customFieldsPendingChanges,
      ),
      true,
    );
  }, [pendingChanges, incomes, customFieldsPendingChanges]);

  const totalPendingChangesCount = getTotalPendingChangesCount({
    pendingChangesCounts,
    data: incomes,
  });

  return (
    <TableWrapper tableName={tableName} onToggle={onToggle}>
      <TableCDS
        withAddButton
        config={config}
        customStyles={tableStyles}
        data={formattedData}
        getCellPendingChangeAuthors={getCellPendingChangeAuthors}
        isCustomDataDefinition={!incomes.length}
        isLoading={isLoading}
        isPreview={!isExpanded}
        pendingChangesCount={totalPendingChangesCount}
        title={FINANCIAL_INFO_TABLE_NAME[tableName]}
        onAddClick={onAddClick}
      />
    </TableWrapper>
  );
};

EmploymentAndIncome.propTypes = {
  ...TablePropTypes,
};

export default withLoading(EmploymentAndIncome);
